import Connector, { type Attachable } from '../objects/Connector';
import type Canvas from '../objects/Canvas';
import { AttachmentPoint, ConnectorEnd } from '../types';
import Shape from '../objects/Shape';
import type ShapeConnectControl from '../controls/ShapeConnectControl';
import { type StylusConnectorOptions } from './StylusConnector';
import { type IPoint } from 'fabric/fabric-impl';
import { type MouseEventData } from '../external-types';
import DraggableBrush from './DraggableBrush';
import { fabric } from 'fabric';
import { calculateAttachmentPosition } from '../utils/connectorAttachment';
import { isShape } from '../objects/guards';

const CLICK_TIMEOUT = 200;
const CLICK_RADIUS = 10;
export const CONNECTOR_OFFSET = 75;
export const NOTE_OFFSET = 45;
const DEFAULT_LENGTH = 75;

interface ClickStart {
  started: number;
  pointer: IPoint;
}

// Brush for drawing connectors.
class ConnectorDrawingBrush extends DraggableBrush {
  private connector: Connector | null = null;
  private clickStart: ClickStart | undefined;

  constructor(
    private readonly canvas: Canvas,
    private readonly initialOptions?: StylusConnectorOptions,
    private readonly shapeControl?: ShapeConnectControl,
    private readonly originalObject?: Shape,
    private readonly drawingComplete?: () => void
  ) {
    super();
  }

  objectInProgress() {
    return this.connector;
  }

  async startDragging(pointer: IPoint) {
    const controller = this.canvas.controller;
    const endPoint = { x: pointer.x + DEFAULT_LENGTH, y: pointer.y };
    const { iconName, ...options } = this.initialOptions ?? {};
    this.connector = Connector.create([pointer, endPoint], {
      controller,
      ...controller.defaultsManager.for('connector'),
      ...options,
    });
    this.connector.beingControlled = true;
    if (iconName) {
      this.connector.createIconLabel({
        iconName,
      });
    }
    this.canvas.setActiveObject(this.connector);
    return this.objectInProgress();
  }

  onMouseDown(pointer: IPoint, eventData: MouseEventData) {
    if (!this.canvas._isMainEvent(eventData.e)) {
      return;
    }

    this.connector = null;
    this.canvas.controller.actionSetStylusMenu(null);

    const attachmentPosition = this.shapeControl
      ? calculateAttachmentPosition(
          this.shapeControl.currentObject,
          this.shapeControl.attachmentPoint,
          { withStandoff: true }
        )
      : undefined;
    this.clickStart = {
      started: Date.now(),
      pointer: attachmentPosition ?? pointer,
    };
  }

  onMouseMove(pointer: IPoint, eventData: MouseEventData) {
    if (!this.canvas._isMainEvent(eventData.e)) {
      return;
    }

    if (!this.connector && this.clickStart) {
      // See if we should start making a dynamic connector.
      if (
        Date.now() > this.clickStart.started + CLICK_TIMEOUT &&
        (Math.abs(pointer.x - this.clickStart.pointer.x) > CLICK_RADIUS ||
          Math.abs(pointer.y - this.clickStart.pointer.y) > CLICK_RADIUS)
      ) {
        this._createConnector({
          eventData,
          drawing: true,
          clickStart: this.clickStart,
        });
      }
    } else {
      this.canvas._transformObject(eventData.e);
    }
  }

  onMouseUp(eventData: MouseEventData) {
    if (!this.canvas._isMainEvent(eventData.e)) {
      return true;
    }

    if (!this.connector && this.clickStart) {
      // The user just clicked without dragging. Create a connector and shape.
      if (this.shapeControl) {
        this.handleClick(this.shapeControl);
      } else {
        const connector = this._createConnector({
          eventData,
          drawing: false,
          clickStart: this.clickStart,
        });
        connector.beingControlled = false;
      }
    } else {
      // The user finished a drag.
      // Note: we don't ever seem to evaluate this block, as it seems "drag end" is now
      // handled elsewhere
      this.canvas._finalizeCurrentTransform(eventData.e);
      this.canvas._currentTransform = null;
    }

    this.shapeControl?.drawingComplete();
    this.drawingComplete?.();
    this.canvas.controller.actionShowObjectToolbar();
    this.canvas.controller.undoManager.completeUndoGroup();

    return false;
  }

  buildShape() {
    const { degreesToRadians, rotateVector } = fabric.util;

    const startObj = this.originalObject;
    const shapeControl = this.shapeControl;

    if (!startObj || !shapeControl) {
      throw new Error(
        'Must provide originalObject and shapeControl in order to use buildShape'
      );
    }

    const normal = shapeControl.normal;

    // See if the starting object already has a connector from this location.
    // If so, offset the next object a bit so they don't overlap.
    const existingAttachments = startObj.connectorsForAttachmentPoint(
      shapeControl.attachmentPoint
    );
    const additionalOffsetFactor = 20 * existingAttachments.length;

    const newShapeName = startObj.shapeName;

    const newShapeOptions = {
      width: startObj.width,
      height: startObj.height,
      scaleX: startObj.scaleX,
      scaleY: startObj.scaleY,
      angle: startObj.angle,
      fill: startObj.fill,
      opacity: startObj.opacity,
      stroke: startObj.stroke,
      strokeWidth: startObj.strokeWidth,
      strokeType: startObj.strokeType,
      fontSize: startObj.fontSize,
      textOpacity: startObj.textOpacity,
      variant: startObj.variant,

      // Special handling for some shapes.
      ...(() => {
        switch (newShapeName) {
          case 'image':
          case 'video':
            return {
              attachmentId: startObj.attachmentId,
              contentType: startObj.contentType,
            };

          case 'record':
            return {
              recordId: startObj.recordId,
              recordType: startObj.recordType,
            };

          case 'smartRecord':
            return {
              recordType: startObj.recordType,
            };

          case 'integrationRecord':
            return {
              integrationRecord: startObj.integrationRecord,
            };

          case 'icon':
          case 'spriteIcon':
            return {
              iconName: startObj.iconName,
              strokeWidth: 0,
            };

          case 'customPath':
            return {
              shapePath: startObj.shapePath,
            };
        }
      })(),
    };

    const newHalfWidth = (newShapeOptions.width * newShapeOptions.scaleX) / 2;
    const newHalfHeight = (newShapeOptions.height * newShapeOptions.scaleY) / 2;
    const startHalfWidth = (startObj.width * startObj.scaleX) / 2;
    const startHalfHeight = (startObj.height * startObj.scaleY) / 2;
    const shapeSpacing =
      newShapeName === 'note' ? NOTE_OFFSET : CONNECTOR_OFFSET;

    const position = {
      x:
        startHalfWidth +
        normal.x * (startHalfWidth + shapeSpacing + newHalfWidth) +
        additionalOffsetFactor -
        newHalfWidth,
      y:
        startHalfHeight +
        normal.y * (startHalfHeight + shapeSpacing + newHalfHeight) +
        additionalOffsetFactor -
        newHalfHeight,
    };
    const angle = degreesToRadians(startObj.angle ?? 0);
    const offset = rotateVector(position, angle);

    return new Shape({
      shapeName: newShapeName,
      authorId: this.canvas.controller.state.userId,
      controller: this.canvas.controller,
      canvas: this.canvas,

      left: startObj.left + offset.x,
      top: startObj.top + offset.y,

      ...newShapeOptions,
    });
  }

  private _createConnector({
    eventData,
    drawing,
    clickStart,
  }: {
    eventData: MouseEventData;
    drawing: boolean;
    clickStart: ClickStart;
  }) {
    const canvas = this.canvas;
    const startPoint = clickStart.pointer;
    let endPoint;
    if (drawing) {
      endPoint = { x: startPoint.x + 0.1, y: startPoint.y + 0.1 };
    } else {
      endPoint = { x: startPoint.x + DEFAULT_LENGTH, y: startPoint.y };
    }

    const { iconName, ...options } = this.initialOptions ?? {};

    const controller = this.canvas.controller;
    this.connector = Connector.create([startPoint, endPoint], {
      controller,
      ...controller.defaultsManager.for('connector'),
      ...options,
      drawing,
    });
    this.connector.beingControlled = true;

    canvas.setActiveObject(this.connector);
    canvas.controller.actionHideObjectToolbar();
    canvas.isDrawingMode = false;

    if (this.shapeControl) {
      // Attach the start of the connector to the shape.
      this.shapeControl.attachStart(this.connector);
    }

    if (drawing) {
      // Stop free drawing and setup the canvas transform information as if
      // the user had started dragging on our new connector.
      canvas.isDrawingMode = false;

      canvas._isCurrentlyDrawing = false;

      this._setupTransform(eventData.e, this.connector);
    }

    if (iconName) {
      this.connector.createIconLabel({
        iconName,
      });
    }

    return this.connector;
  }

  _setupTransform(e: Event, connector: Connector) {
    connector.__corner = 'p1';
    this.canvas._setupCurrentTransform(e, connector, true);
  }

  private handleClick(shapeControl: ShapeConnectControl) {
    const sourceObj = this.originalObject;

    if (!sourceObj) {
      console.error('Must create connector from existing shape');
      return;
    }

    // Placeholder object to select after creating the connector.
    let obj: fabric.Object;

    // Check if we should connect to nearby shape
    if (shapeControl.nearbyShape && shapeControl.targetAttachmentPoint) {
      obj = shapeControl.nearbyShape;
      this.createConnectorBetweenShapes(
        sourceObj,
        shapeControl.nearbyShape,
        shapeControl.attachmentPoint,
        shapeControl.targetAttachmentPoint
      );
    } else {
      // Create the shape first, and then add the connector to the new shape. We
      // do it in this order so in the future we can show a shadow of the shape
      // before it is created and use the existing logic for routing the
      // connector to the shape.

      const newShape = this.buildShape();
      const controller = this.canvas.controller;

      // Mark the new shape as newly created to enable Tab key sequential creation
      if (isShape(newShape)) {
        newShape.isNewlyCreated = true;
      }

      controller.addToCanvas(newShape);
      const connectorOptions = {
        controller,
        ...controller.defaultsManager.for('connector'),
      };

      if (sourceObj.angle) {
        // Circular import prevents us importing ConnectorRouting.ROUTING_BEZIER const
        connectorOptions.lineRouting = 10;
      }

      // Sticky notes do not get connectors when just clicking to create.
      if (isShape(newShape) && newShape.shapeName !== 'note') {
        this.connector = Connector.create(
          [
            { x: 0, y: 0 },
            { x: 1, y: 1 },
          ],
          connectorOptions
        );

        // Attach the connector
        sourceObj.attachConnector(
          this.connector,
          ConnectorEnd.CONNECTOR_START,
          shapeControl.attachmentPoint
        );
        newShape.attachConnector(
          this.connector,
          ConnectorEnd.CONNECTOR_END,
          shapeControl.oppositeAttachmentPoint ?? AttachmentPoint.Center
        );
        this.connector.constrainConnector({});
      }

      obj = newShape;
    }

    // Select the shape rather than the connector.
    this.canvas.setActiveObject(obj);
  }

  private createConnectorBetweenShapes(
    sourceShape: Attachable,
    targetShape: Attachable,
    sourceAttachment: AttachmentPoint,
    targetAttachment: AttachmentPoint
  ): Connector {
    // For this particular use case, prefer center attachment points for both shapes.
    // The center point gives us auto-adjustment behavior, which we prioritize
    // for the target shape. Having both shapes use center if possible feels a
    // bit more consistent and satisfying.
    if (sourceShape.attachmentPoints().includes(AttachmentPoint.Center)) {
      sourceAttachment = AttachmentPoint.Center;
    }

    // Get attachment positions using indicator method since connector doesn't exist yet
    const startPoint = calculateAttachmentPosition(
      sourceShape,
      sourceAttachment,
      { withStandoff: true }
    );

    const endPoint = calculateAttachmentPosition(
      targetShape,
      targetAttachment,
      { withStandoff: true }
    );

    if (!startPoint || !endPoint) {
      throw new Error('Could not calculate attachment positions');
    }

    // Create connector
    const controller = this.canvas.controller;
    const connector = Connector.create([startPoint, endPoint], {
      controller,
      ...controller.defaultsManager.for('connector'),
    });

    // Attach to both shapes
    sourceShape.attachConnector(
      connector,
      ConnectorEnd.CONNECTOR_START,
      sourceAttachment
    );
    targetShape.attachConnector(
      connector,
      ConnectorEnd.CONNECTOR_END,
      targetAttachment
    );

    // Add to canvas
    this.canvas.add(connector);

    connector.constrainConnector({});

    return connector;
  }

  _render() {}
}

export default ConnectorDrawingBrush;
